import { useContext, useEffect, useState, useRef } from "react";
import { Modal, Alert, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import "./style.css";
import Pagination from "../Pagination";

const DataTable = ({ context, title, itemName = "", pageSize = 10, alertDuration = 3000 }) => {
  const { t } = useTranslation();

  const contextData = useContext(context || {});

  const [showModal, setShowModal] = useState(false);
  const handleShowModal = () => setShowModal(true);
  const handleHideModal = () => setShowModal(false);

  const [showAlert, setShowAlert] = useState(false);
  const isFirstRender = useRef(true);

  const { items = [], headers = [], itemComponent, addItemForm: AddItemForm } = contextData || {};

  // Stato per la ricerca testuale
  const [searchTerm, setSearchTerm] = useState("");

  // Stato unico dinamico per il numero di righe e pagina corrente
  const [itemsPerPage, setItemsPerPage] = useState(pageSize);
  const [currPage, setCurrPage] = useState(1);

  useEffect(() => {
    setItemsPerPage(pageSize);
  }, [pageSize]);

  // Lista dei campi visibili e testuali su cui eseguire il filtro
  const searchFields = [
    "geom_id",
    "objectid_1",
    "objectid",
    "idpresenze",
    "nome",
    "tipologia",
    "comune",
    "localit�",
    "localita",
    "stato_di_conservazione",
    "stato_conservazione",
    "compilatore",
    "visibilit�",
    "visibilita",
    "descrizione", // <-- Incluso nella ricerca ma non in tabella
    "note",        // <-- Incluso nella ricerca ma non in tabella
    "email",       // Supporto per UserMan
    "name",
    "role",
  ];

  // FILTRAGGIO MIRATO: controlla solo i campi definiti in searchFields
  const filteredItems = items.filter((item) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();

    return searchFields.some((field) => {
      const val = item[field];
      if (val === null || val === undefined) return false;
      return String(val).toLowerCase().includes(term);
    });
  });

  // Reset della pagina a 1 quando si digita nella ricerca
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrPage(1);
  };

  // Calcolo del limite e delle pagine totali sui dati FILTRATI
  const limit = itemsPerPage === "all" ? filteredItems.length : Number(itemsPerPage);
  const totPages = limit > 0 ? Math.ceil((filteredItems?.length || 0) / limit) : 1;

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    setShowAlert(true);
    handleHideModal();

    const timer = setTimeout(() => {
      setShowAlert(false);
    }, alertDuration);

    return () => clearTimeout(timer);
  }, [items, alertDuration]);

  useEffect(() => {
    if (currPage > totPages && totPages > 0) {
      setCurrPage(1);
    }
  }, [filteredItems.length, currPage, totPages]);

  if (!context) {
    console.error("DataTable: 'context' prop is undefined. Please pass a valid React Context.");
    return null;
  }

  // Estrazione degli elementi della pagina corrente dai dati filtrati
  const lastItem = currPage * limit;
  const firstItem = lastItem - limit;
  const currItems = itemsPerPage === "all" ? filteredItems : filteredItems.slice(firstItem, lastItem);

  return (
    <>
      {/* Intestazione e Controlli (Titolo, Ricerca, Selettore righe) */}
      <div className="table-title d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
        <h2 className="mb-0 d-flex align-items-center">
          {title}
          <OverlayTrigger
            overlay={<Tooltip id={`tooltip-top`}>{`${t("add")} ${itemName}`}</Tooltip>}
          >
            <button className="btn btn-title ms-2" onClick={handleShowModal}>
              <i className="material-icons icon-title">&#xE147;</i>
            </button>
          </OverlayTrigger>
        </h2>

        <div className="d-flex align-items-center gap-3">
          {/* Form di Ricerca Testuale */}
          <div className="input-group input-group-sm" style={{ width: "250px" }}>
            <span className="input-group-text bg-secondary text-light border-secondary">
              <i className="material-icons" style={{ fontSize: "16px" }}>search</i>
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Cerca nei campi e note..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
            {searchTerm && (
              <button
                className="btn btn-outline-secondary text-light"
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  setCurrPage(1);
                }}
              >
                &times;
              </button>
            )}
          </div>

          {/* Selettore Righe per Pagina */}
          <div className="d-flex align-items-center">
            <label className="me-2 mb-0 text-light fw-bold" style={{ fontSize: "0.9rem", whiteSpace: "nowrap" }}>
              Righe:
            </label>
            <select
              className="form-select form-select-sm"
              style={{ width: "auto" }}
              value={itemsPerPage}
              onChange={(e) => {
                const val = e.target.value === "all" ? "all" : Number(e.target.value);
                setItemsPerPage(val);
                setCurrPage(1);
              }}
            >
              <option value={10}>10</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value="all">Tutte ({filteredItems.length})</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tabella Dati */}
      <table className="table table-hover table-dark">
        <thead>
          <tr className="bg-primary">
            {headers.map((header, index) => (
              <th key={index}>{header}</th>
            ))}
            <th> </th>
          </tr>
        </thead>
        <tbody>
          {currItems && currItems.length > 0 ? (
            currItems.map((item, index) => (
              <tr key={item.geom_id || item.objectid_1 || item.site_id || item.user_id || item.id || index}>
                {itemComponent && itemComponent({ item })}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={headers.length + 1} className="text-center py-3">
                {searchTerm
                  ? `Nessun risultato trovato per "${searchTerm}"`
                  : t("no_data_available") || "Nessun dato disponibile"}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Paginatore */}
      {itemsPerPage !== "all" && totPages > 1 && (
        <Pagination totPages={totPages} setCurrPage={setCurrPage} currPage={currPage} />
      )}

      {/* Alert Aggiornamento */}
      <Alert show={showAlert} variant="success">
        {`${t("table_updated") || "Tabella aggiornata"}`}
      </Alert>

      {/* Modal Inserimento */}
      <Modal show={showModal} onHide={handleHideModal}>
        <Modal.Header closeButton>
          <Modal.Title>{`${t("add")} ${itemName}`}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {AddItemForm && <AddItemForm handleClose={handleHideModal} />}
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-warning" onClick={handleHideModal}>
            {t("close")}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DataTable;